/** ABM List search helpers — debounce, stale-response guard, empty states. */

export const SEARCH_DEBOUNCE_MS = 300;

export function normalizeSearchQuery(value) {
  return String(value ?? "").trim();
}

/** Returns the next query to apply, or null when unchanged. */
export function resolveDebouncedQuery(inputValue, currentQuery) {
  const next = normalizeSearchQuery(inputValue);
  if (next === normalizeSearchQuery(currentQuery)) return null;
  return next;
}

/** Only apply fetch results when this response matches the latest in-flight request. */
export function shouldApplyLoadResponse(requestId, latestRequestId) {
  return requestId === latestRequestId;
}

export function getAbmEmptyMessage({ hasLoaded, query, hasAnyLeadsInWorkspace }) {
  if (!hasLoaded) {
    return "No ABM List leads yet. Download the template, fill in your leads, and upload the Excel file.";
  }
  if (normalizeSearchQuery(query)) {
    return "No ABM List leads match your search.";
  }
  if (!hasAnyLeadsInWorkspace) {
    return "No ABM List leads yet. Download the template, fill in your leads, and upload the Excel file.";
  }
  return "No ABM List leads on this page.";
}

/**
 * Debounce helper for unit tests and optional imperative scheduling.
 * Rapid schedule() calls coalesce into a single onApply after debounceMs idle.
 */
export function createDebouncedSearchScheduler({ debounceMs, onApply }) {
  let timer = null;

  const cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return {
    schedule(value) {
      cancel();
      timer = setTimeout(() => {
        timer = null;
        onApply(value);
      }, debounceMs);
    },
    flush(value) {
      cancel();
      onApply(value);
    },
    cancel,
  };
}
